import { Card } from "../Card.js";
import { Failed, Loading } from "../ViewState.js";
import { formatKwh } from "../../lib/energy.js";
import { formatAmount } from "../../lib/format.js";
import { cn } from "../../lib/cn.js";
import type { Async } from "../../lib/useAsync.js";

export interface LedgerRow {
  period: string;
  solarKwh: number | null;
  gridKwh: number | null;
  deliveredKwh: number | null;
  chargedKwh: number | null;
  dischargedKwh: number | null;
  /** What the grid import cost, or null when part of it has no tariff. */
  cost: number | null;
  /** How much of the period is missing, or null when none of it is. */
  note: string | null;
}

/** A period's total, and its rows beneath it. */
export interface Ledger {
  total: LedgerRow;
  rows: readonly LedgerRow[];
}

const COLUMNS = [
  { key: "solarKwh", label: "From the panels" },
  { key: "gridKwh", label: "From the grid" },
  { key: "deliveredKwh", label: "Delivered" },
  { key: "chargedKwh", label: "Into the battery" },
  { key: "dischargedKwh", label: "Out of the battery" },
] as const;

/**
 * The design's ledger: the total for the period chosen above, then one row
 * per day or month of it.
 *
 * Two departures from the design, both required rather than preferred. A
 * "From the grid" column, because grid import is the first of the energy
 * figures `REQUIREMENTS.md` section 1 asks for and the headline of v1
 * (docs/DECISIONS/0010), and the design's columns had nowhere to put it. And
 * below the tablet breakpoint each row becomes a stacked card, as section 4
 * requires of every data table -- 4b's version scrolled sideways inside its
 * card, which passed only because it never had a row to show.
 *
 * The cost column is what the grid import cost, each quarter hour priced at
 * the tariff in force (docs/DECISIONS/0033). A dash, never a zero, where part
 * of the row falls before the first tariff.
 */
export function LedgerTable({
  ledger,
  onRetry,
  currency,
}: {
  ledger: Async<Ledger>;
  onRetry: () => void;
  currency: string;
}) {
  return (
    <Card title="Production ledger">
      {ledger.kind === "loading" ? (
        <Loading label="Loading the ledger…" />
      ) : ledger.kind === "error" ? (
        <Failed message={ledger.message} onRetry={onRetry} />
      ) : ledger.value.rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-ink-muted">
          Nothing has been recorded yet. Rows appear here once the inverter has reported.
        </p>
      ) : (
        <>
          <LedgerCards ledger={ledger.value} currency={currency} />
          <LedgerGrid ledger={ledger.value} currency={currency} />
        </>
      )}
    </Card>
  );
}

/** Below the tablet breakpoint: one stacked card per row, the total first. */
function LedgerCards({ ledger, currency }: { ledger: Ledger; currency: string }) {
  return (
    <ul className="flex flex-col divide-y divide-line md:hidden">
      {[ledger.total, ...ledger.rows].map((row) => (
        <li key={row.period} className={cn("py-4", row === ledger.total && "font-semibold")}>
          <Period row={row} />
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            {COLUMNS.map((column) => (
              <div key={column.key} className="contents">
                <dt className="text-ink-muted">{column.label}</dt>
                <dd className="tabular text-right text-ink">{formatKwh(row[column.key])}</dd>
              </div>
            ))}
            <dt className="text-ink-muted">{`Grid cost (${currency})`}</dt>
            <dd className="tabular text-right text-ink">{formatAmount(row.cost)}</dd>
          </dl>
        </li>
      ))}
    </ul>
  );
}

/** From the tablet breakpoint up: the design's table, the total its first row. */
function LedgerGrid({ ledger, currency }: { ledger: Ledger; currency: string }) {
  return (
    <table className="hidden w-full text-left text-sm md:table">
      <thead>
        <tr className="border-b border-line text-xs font-medium text-ink-muted">
          <th scope="col" className="pb-4 font-normal">
            Period
          </th>
          {COLUMNS.map((column) => (
            <th key={column.key} scope="col" className="pb-4 pr-4 font-normal">
              {column.label}
            </th>
          ))}
          <th scope="col" className="pb-4 text-right font-normal">
            {`Grid cost (${currency})`}
          </th>
        </tr>
      </thead>
      <tbody className="tabular divide-y divide-line">
        {[ledger.total, ...ledger.rows].map((row) => (
          <tr
            key={row.period}
            className={cn(
              "transition-colors hover:bg-line/40",
              row === ledger.total && "font-semibold",
            )}
          >
            <th scope="row" className="py-4 text-left font-medium text-ink">
              <Period row={row} />
            </th>
            {COLUMNS.map((column) => (
              <td
                key={column.key}
                className={cn("py-4 pr-4", row === ledger.total ? "text-ink" : "text-ink-muted")}
              >
                {formatKwh(row[column.key])}
              </td>
            ))}
            <td
              className={cn(
                "py-4 text-right",
                row === ledger.total ? "text-ink" : "text-ink-muted",
              )}
            >
              {formatAmount(row.cost)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The period's name, and beneath it how much of it is missing. */
function Period({ row }: { row: LedgerRow }) {
  return (
    <span className="flex flex-col">
      <span className="font-medium text-ink">{row.period}</span>
      {row.note !== null && <span className="text-xs font-normal text-ink-muted">{row.note}</span>}
    </span>
  );
}
